// IMPORTANT: Import global less first to avoid CSS cascading issues
import '../less/_app.global.less';

import 'src/lib/polyfills';
import 'src/lib-server/django-shims';
import {QueryClient, QueryClientProvider, Hydrate} from '@tanstack/react-query';
import {useStaticRendering} from 'mobx-react-lite';
import NextApp, {AppContext} from 'next/app';
import {Router} from 'next/router';
import {useEffect, useState} from 'react';

import {CommonStoresProvider} from '@udemy/common-stores';
import {detectKeyboardNavigation} from '@udemy/design-system-utils';
import {I18nProvider, resolveLocaleDirection, useI18n, UdemyLocale} from '@udemy/i18n';
import {
    APP_CONTEXT_VAR_NAME,
    getClientAppContext,
    mergeWithArrayOverwrite,
} from '@udemy/nextjs-core';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {shimUDPerformance} from '@udemy/performance-rum-client';
import {ExperimentationProvider} from '@udemy/react-experimentation-provider';
import {ShoppingClient} from '@udemy/shopping';
import {StoreProvider} from '@udemy/store-provider';
import {udApi} from '@udemy/ud-api';
import {UDData, UDDataConfig, UDDataProvider, useUDData, useUDLink} from '@udemy/ud-data';

import {i18nPathToBundleMap, i18nPreferred} from 'next.config.i18n';
import {BindI18nGlobals} from 'src/components/bind-i18n-globals/bind-i18n-globals';
import {AppWithLayoutProps} from 'src/components/layout/types';
import {useDevice} from 'src/hooks/useDevice';
import {IntegrationsStore} from 'src/stores/integrations-store';
import {RootStore} from 'src/stores/root-store';

import {
    AUTH_FULL_PAGE_REDIRECT,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    QUICK_VIEW_BOX_FEATURE,
    INDEED_LINKS_ON_AGGREGATOR_PAGES,
    CERT_VOUCHERS_SHOW_ADD_TO_CART,
    SHOW_VALUE_STARTER_PLAN,
} from '../lib/experimentation-constants';

// Requests are proxied through Next app, so use relative URL for API calls
const DEPLOY_ENV = process.env.DEPLOY_ENV;
if (DEPLOY_ENV === 'local' || DEPLOY_ENV === 'dev') {
    udApi.configure({
        getBaseUrl: () => `${process.env.APP_BASE_PATH}api-2.0`,
    });
}

// TODO: rehyrdate from pageProps instead
const queryClient = new QueryClient();

/**
 * Prevent observable components to re-render on the server. Other wise this causes a memory leak since lots of mobx
 * references being created on the server and never disposed.
 * @see https://github.com/mobxjs/mobx/tree/main/packages/mobx-react-lite#enablestaticrenderingenable-true
 */
// eslint-disable-next-line react-hooks/rules-of-hooks
useStaticRendering(typeof window === 'undefined');

// List of experimentation features to enable
// See https://github.com/udemy/frontends-components/tree/main/packages/react-experimentation-provider
export const EXPERIMENTATION_FEATURES = [
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    AUTH_FULL_PAGE_REDIRECT,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    QUICK_VIEW_BOX_FEATURE,
    INDEED_LINKS_ON_AGGREGATOR_PAGES,
    CERT_VOUCHERS_SHOW_ADD_TO_CART,
    SHOW_VALUE_STARTER_PLAN,
];
const I18nAccessBootstrapper = () => {
    const i18n = useI18n();
    const udData = useUDData();
    const udLink = useUDLink();

    useEffect(() => {
        if (!udData.isGlobalMeContextLoading) {
            ShoppingClient.initialize({
                i18n,
                udLink,
                isShoppingCartFeatureEnabled: udData.Config.features.shopping_cart,
            });

            if (udData.me.is_authenticated && udData.me.id) {
                ShoppingClient.setUserId(udData.me.id);
            }

            ShoppingClient.fetch();
        }
    }, [i18n, udData, udLink]);

    return null;
};

/**
 *
 * This hook modifies the default behavior of Next.js for data fetching, specifically prefetching for pages via Link component.
 *
 * Problem:
 * Next.js v12 does not correctly handle /_next/data calls when using ISR (Incremental Static Regeneration) with assetPrefix.
 * Calls are made to www.udemy.com/_next/data/<patH> which results in a 404 error since requests areproxied through CF
 * like udemy.com/APP-NAME/<path>.
 *
 * This problem mainly occurs when prefetching pages via the Next.js Link component. https://nextjs.org/docs/pages/api-reference/components/link
 * Even if Next.js handles /_next/data/ with the correct prefix, we won't have pre-generated pages (build) on the CDN.
 *
 * Discussion on this: https://github.com/vercel/next.js/discussions/25681
 *
 * Solution:
 * Patch the getDataHref function to prepend the APP_NAME to the URL, enabling client-side navigation through Cloudflare,
 * as all requests are proxied through CF like udemy.com/APP-NAME/<path>.
 *
 * NOTE: Revisit this after upgrading to Next.js 13 or later. According to the Next.js developers, this issue should not
 * occur with the app router.
 */
export const useInterceptNextDataHref = (router: Router) => {
    // Start the interception on client, do not intercept on server
    const pageLoader = router.pageLoader;
    if (pageLoader?.getDataHref) {
        // Store the original getDataHref function
        const originalGetDataHref = pageLoader.getDataHref;
        // Override the getDataHref function
        pageLoader.getDataHref = function (...args: Parameters<typeof originalGetDataHref>) {
            // Call the original getDataHref function
            const r = originalGetDataHref.call(pageLoader, ...args);
            // If the URL starts with /_next/data, prepend the APP_NAME to the URL
            return r && r.startsWith('/_next/data/') ? `/${process.env.APP_NAME}${r}` : r;
        };
    }
};

export default function App({
    Component,
    locale, // NOTE: this is just the language portion of the locale, i.e. en instead of en_US
    pageProps,
    router,
}: AppWithLayoutProps) {
    // Get application context and render mode
    const {appContext, renderMode} = pageProps;

    // Patch getDataHref to fix prefetching for pages via Link component
    useInterceptNextDataHref(router);

    // resolve full locale
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';

    // Initialize root store
    const [rootStore] = useState(
        () =>
            new RootStore({
                footerData: appContext.footer,
                headerData: appContext.header,
                locale: fullLocale,
            }),
    );
    const [integrationsStore] = useState(() => new IntegrationsStore());

    // Shim device types
    const {isMobile, isDesktop, isTablet} = useDevice();

    // local UD data state
    const [udData, setUDData] = useState(() => {
        const initUDData = mergeWithArrayOverwrite(appContext.udData, {
            Config: {
                app_name: process.env.APP_NAME,
                features: {
                    notice: {
                        smart_bar: true,
                    },
                },
            } as unknown as UDDataConfig,
            userAgnosticTrackingParams: {
                page_key: pageProps.pageKey,
            },
            isGlobalMeContextLoading: true,
            request: {
                isMobile: isMobile,
                isPC: isDesktop,
                isTablet: isTablet,
                language: locale,
                locale: fullLocale,
            },
        });
        return initUDData;
    });

    // Shim UD.performance global API
    shimUDPerformance();

    useEffect(() => {
        async function bootstrapClient() {
            // Get client UD data.  If we are in SSR mode, the data is already available in scope
            const {udData: clientUDData} =
                renderMode === RENDER_MODES.SSR ? await {udData} : await getClientAppContext();

            const fullUdData: UDData = mergeWithArrayOverwrite(udData, clientUDData);

            // Ensure we've set user context has loaded flag
            const fullUdDataWithOverrides: UDData = mergeWithArrayOverwrite(fullUdData, {
                /* needed to enable smart-bar */
                Config: {
                    features: {
                        notice: {
                            smart_bar: true,
                        },
                    },
                },
            });

            // Update udData
            setUDData(fullUdDataWithOverrides);

            // If you are SSRing the page, you need to write global UD data to the window object.
            // This will be eventually replaced by a standard app context provider.
            if (renderMode === RENDER_MODES.SSR) {
                const appContextData = {
                    footer: appContext.footer,
                    header: appContext.header,
                    udData: fullUdDataWithOverrides,
                };
                Object.assign(window, {
                    [APP_CONTEXT_VAR_NAME]: appContextData,
                });
            }

            // Initialize application stores
            rootStore.initializeClient();
            integrationsStore.initializeClient();

            // Turn on keyboard navigation detection
            detectKeyboardNavigation();

            return fullUdDataWithOverrides;
        }

        bootstrapClient();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Use the layout defined at the page level, if available
    // https://nextjs.org/docs/basic-features/layouts#per-page-layouts
    const getLayout = Component.getLayout ?? ((page) => page);

    return (
        <>
            <I18nProvider lang={locale} locale={fullLocale} translations={pageProps.translations}>
                <UDDataProvider data={udData as UDData} writeToGlobal={true}>
                    <CommonStoresProvider>
                        <StoreProvider stores={[rootStore, integrationsStore]}>
                            <QueryClientProvider client={queryClient}>
                                <Hydrate state={pageProps.dehydratedState}>
                                    <ExperimentationProvider features={EXPERIMENTATION_FEATURES}>
                                        <BindI18nGlobals />
                                        <I18nAccessBootstrapper />
                                        {getLayout(<Component {...pageProps} />)}
                                    </ExperimentationProvider>
                                </Hydrate>
                            </QueryClientProvider>
                        </StoreProvider>
                    </CommonStoresProvider>
                </UDDataProvider>
            </I18nProvider>
        </>
    );
}

// Do not resolve anything user specific in this function.
App.getInitialProps = async function (appContext: AppContext) {
    /**
     * Required step from https://nextjs.org/docs/advanced-features/custom-app#caveats
     *
     * When you add getInitialProps in your custom app, you must import App from "next/app",
     * call App.getInitialProps(appContext) inside getInitialProps and merge the returned object into the return value.
     */
    const locale = appContext.router.locale as string;
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';
    const [nextAppContext] = await Promise.all([NextApp.getInitialProps(appContext)]);
    return {
        ...nextAppContext,
        locale,
        pageProps: {
            ...nextAppContext.pageProps,
            direction: resolveLocaleDirection(fullLocale as UdemyLocale),
        },
    };
};
